import React, { useEffect } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { useStyles } from "./styles"
import {
  AutocompleteChangeReason,
  AutocompleteRenderOptionState,
  Box
} from "@mui/material"
import { Option } from "../../../mp-shared-components/generic-shared-components/components/Autocomplete/Autocomplete"
import { UserGlobalSearchOption } from "./components"
import { useGlobalSearchData } from "./hooks"
import { User as GlobalSearchUser } from "../../../../types/gqlTypes/graphql"
import { convertUsersToOptions, getSelectedUsersPagePath } from "./helpers"
import { SearchWithDropdown } from "../../../mp-shared-components/generic-shared-components/components"
import { SearchAlertMessage } from "../../../mp-shared-components/generic-shared-components/components/SearchWithDropdown/components"

interface Props {}

const UserGlobalSearch: React.FC<Props> = () => {
  const {
    loading,
    searchInput,
    searchedUsers,
    isNarrowSearch,
    onSearchInputChange,
    clearSearchInput
  } = useGlobalSearchData()

  const { classes } = useStyles()

  const location = useLocation()
  const history = useHistory()

  useEffect(() => {
    clearSearchInput()
  }, [location.pathname])

  const setNewParams = (user: GlobalSearchUser) => {
    const selectedUsersPagePath = getSelectedUsersPagePath(
      user,
      location.search
    )

    if (!selectedUsersPagePath) {
      console.warn("Incorrect path to selected user!")
      return
    }

    history.push(selectedUsersPagePath)
    clearSearchInput()
  }

  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement>,
    option: Option,
    _state: AutocompleteRenderOptionState
  ) => {
    const { value } = option

    if (value === "narrow" || value === "no-results") {
      return <SearchAlertMessage key={optionProps.id} type={value} />
    }

    const searchedUser = searchedUsers.find(user => user.id === value)

    return (
      <UserGlobalSearchOption
        key={optionProps.id}
        user={searchedUser}
        autocompleteOptionProps={optionProps}
        onClick={() => setNewParams(searchedUser)}
      />
    )
  }

  const onChange = (
    _event: React.SyntheticEvent,
    option: NonNullable<string | Option> | (string | Option)[],
    reason: AutocompleteChangeReason
  ) => {
    if (reason === "selectOption") {
      const searchedUser = searchedUsers.find(user => user.id === option)

      !!searchedUser && setNewParams(searchedUser)
    }
  }

  const options = convertUsersToOptions(searchedUsers)

  return (
    <Box className={classes.searchContainer}>
      <SearchWithDropdown
        options={options}
        searchInput={searchInput}
        placeholder="Search Users"
        loading={loading}
        isNoResults={options.length === 0 && !!searchInput}
        isNarrowSearch={isNarrowSearch && !!searchInput}
        onChange={onChange}
        renderOption={renderOption}
        onSearchInputChange={onSearchInputChange}
        data-automation-id="users-global-search"
      />
    </Box>
  )
}

export default UserGlobalSearch
